import { useParams, Link } from 'react-router';
import { useSiteConfig } from '../utils/siteConfig';
import { motion } from 'motion/react';
import { ArrowLeft, BookOpen } from 'lucide-react';

export function CustomPage() {
  const { slug } = useParams<{ slug: string }>();
  const { config } = useSiteConfig();

  const page = config.customPages.find(p => p.slug === slug && p.isPublished);

  if (!page) {
    return (
      <div className="pt-32 pb-32 min-h-screen bg-slate-50 flex items-center justify-center text-center px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md p-8 bg-white rounded-3xl border border-slate-200/60 shadow-sm"
        >
          <h2 className="text-3xl font-black mb-4 text-slate-900">Page Not Found</h2>
          <p className="text-slate-500 mb-8">The custom page you are trying to view does not exist or has been unpublished.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all">
            <ArrowLeft size={18} /> Go back home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-32 min-h-screen bg-slate-50 text-slate-900">
      <motion.article 
        className="max-w-4xl mx-auto px-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/" className="inline-flex items-center gap-2 text-primary font-semibold hover:text-accent transition-colors mb-8">
          <ArrowLeft size={18} /> Back to home
        </Link>

        <div className="bg-white p-8 md:p-16 rounded-[2.5rem] border border-slate-100/80 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <div className="w-12 h-12 bg-primary/5 text-primary rounded-xl flex items-center justify-center mb-6">
              <BookOpen size={24} />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-8">
              {page.title}
            </h1>
            
            <div 
              className="prose prose-slate max-w-none prose-headings:font-bold prose-h3:text-xl prose-p:text-slate-600 prose-p:leading-relaxed prose-li:text-slate-600 prose-ul:list-disc prose-ul:pl-6 space-y-4"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          </div>
        </div>
      </motion.article>
    </div>
  );
}
